import { client, gql } from 'data-provider';
import { IReportDashboard, IReport } from 'interfaces';

// Sentinel categoryId meaning "products with no category assigned" - must match
// UNCATEGORIZED_CATEGORY_ID in erp-api's report.service.ts. Not a real category id,
// so it's never returned by the category search and must be offered as a fixed option.
export const UNCATEGORIZED_CATEGORY_ID = 'UNCATEGORIZED';

export const getDashboardReportsInfo = async (where: {
  startDate: string;
  endDate: string;
  tagIds?: string[];
  categoryId?: string;
}) => {
  const query = gql`
    query ($where: JSON) {
      findDashboardCounts(where: $where) {
        purchase
        sales
        expense
        damage
        return
        discount
        grossProfit
        netProfit
      }
    }
  `;

  const variables = { where };
  const data = await client.request(query, variables);
  return data.findDashboardCounts as IReportDashboard;
};

export interface IAllReportQuery {
  startDate?: string;
  endDate?: string;
  searchString?: string;
  tagIds?: string[];
  categoryId?: string;
  start?: number;
  limit?: number;
}

export interface IReportResponse {
  data: IReport[];
  page: number;
  size: number;
  total: number;
}

export const getPurchaseReports = async (reportQuery?: IAllReportQuery) => {
  const where = {
    query: reportQuery && reportQuery.searchString ? reportQuery.searchString : '',
    startDate: reportQuery && reportQuery.startDate ? reportQuery.startDate : undefined,
    endDate: reportQuery && reportQuery.endDate ? reportQuery.endDate : undefined,
    tagIds: reportQuery?.tagIds,
    categoryId: reportQuery?.categoryId,
  };
  const query = gql`
    query ($where: JSON, $sort: SortInput, $start: Int, $limit: Int) {
      PurchaseReport(where: $where, sort: $sort, start: $start, limit: $limit) {
        data {
          id
          orderType
          name
          amount
          date
        }
        page
        size
        total
      }
    }
  `;

  const variables = {
    where,
    start: reportQuery && reportQuery.start ? reportQuery.start : 1,
    limit: reportQuery && reportQuery.limit ? reportQuery.limit : 10,
  };
  const data = await client.request(query, variables);
  return data.PurchaseReport as IReportResponse;
};

export const getSalesReports = async (reportQuery?: IAllReportQuery) => {
  const where = {
    query: reportQuery && reportQuery.searchString ? reportQuery.searchString : '',
    startDate: reportQuery && reportQuery.startDate ? reportQuery.startDate : undefined,
    endDate: reportQuery && reportQuery.endDate ? reportQuery.endDate : undefined,
    tagIds: reportQuery?.tagIds,
    categoryId: reportQuery?.categoryId,
  };

  const query = gql`
    query ($where: JSON, $sort: SortInput, $start: Int, $limit: Int) {
      SalesReport(where: $where, sort: $sort, start: $start, limit: $limit) {
        data {
          id
          orderType
          name
          amount
          date
        }
        page
        size
        total
      }
    }
  `;

  const variables = {
    where,
    start: reportQuery && reportQuery.start ? reportQuery.start : 1,
    limit: reportQuery && reportQuery.limit ? reportQuery.limit : 10,
  };
  const data = await client.request(query, variables);
  return data.SalesReport as IReportResponse;
};

export interface IPosPaymentChannelQuery {
  startDate?: string | null;
  endDate?: string | null;
  channel?: string | null;
  provider?: string | null;
  page?: number;
  limit?: number;
}

export interface IPosPaymentChannelReportResponse {
  byChannel: {
    channel: string;
    count: number;
    totalAmount: number;
    pctOfTotal: number;
  }[];
  byProvider: {
    channel: string;
    provider: string;
    count: number;
    totalAmount: number;
  }[];
  details: {
    id: string;
    salesCopyId: string;
    salesDate: string;
    paymentChannel: string;
    paymentProvider: string;
    paymentDetails: string;
    currentPaidAmount: number;
    clientName: string;
    warehouseName: string;
  }[];
  pagination: {
    total: number;
    page: number;
    size: number;
  };
}

export const getPosPaymentChannelReport = async (queryVariables: IPosPaymentChannelQuery) => {
  const query = gql`
    query PosPaymentChannelReport(
      $startDate: DateTime
      $endDate: DateTime
      $channel: PaymentChannel
      $provider: String
      $page: Int
      $limit: Int
    ) {
      PosPaymentChannelReport(
        startDate: $startDate
        endDate: $endDate
        channel: $channel
        provider: $provider
        page: $page
        limit: $limit
      ) {
        byChannel {
          channel
          count
          totalAmount
          pctOfTotal
        }
        byProvider {
          channel
          provider
          count
          totalAmount
        }
        details {
          id
          salesCopyId
          salesDate
          paymentChannel
          paymentProvider
          paymentDetails
          currentPaidAmount
          clientName
          warehouseName
        }
        pagination {
          total
          page
          size
        }
      }
    }
  `;

  const data = await client.request(query, queryVariables);
  return data.PosPaymentChannelReport as IPosPaymentChannelReportResponse;
};
